import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Check, Send, Sparkles, Shield, Droplets, HelpCircle, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: 'tinting', label: 'Window Tinting', icon: Shield, blurb: 'Legal, precision-cut tint with premium films and lifetime workmanship.' },
  { id: 'stage1', label: 'Paint Correction — Stage 1', sub: 'Enhancement Polish', icon: Sparkles, blurb: 'Single-stage enhancement polish that revives gloss and removes light haze.' },
  { id: 'stage2', label: 'Paint Correction — Stage 2', sub: 'Swirl & Scratch Removal', icon: Sparkles, blurb: 'Multi-stage machine correction that removes swirls, scratches and oxidation.' },
  { id: 'ceramic', label: 'Ceramic Coating', icon: Droplets, blurb: 'Long-term gloss and hydrophobic protection, applied and cured on-site.' },
  { id: 'other', label: 'Other / Not Sure', sub: '', icon: HelpCircle, blurb: 'Something else in mind? Tell us what your car needs and we will advise.' },
];

const SpecialServices = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', suburb: '',
    make: '', model: '', year: '',
    services: [], message: '',
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleService = (label) => setForm(p => ({
    ...p,
    services: p.services.includes(label) ? p.services.filter(x => x !== label) : [...p.services, label],
  }));

  const submit = async () => {
    if (!form.name || !form.email || !form.phone || form.services.length === 0) {
      toast.error('Please fill in name, email, phone and pick at least one service');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        vehicleType: 'other',
        vehicleMake: form.make,
        vehicleModel: form.model,
        address: form.suburb,
        preferredDate: '',
        preferredTime: '',
        selectedPackage: `Special Services Enquiry — ${form.services.join(' + ')}`,
        selectedAddOns: [],
        specialRequests: [
          `[Special Services Enquiry]`,
          `Services:`,
          ...form.services.map(s => `- ${s}`),
          [form.year, form.make, form.model].filter(Boolean).length ? `Vehicle: ${[form.year, form.make, form.model].filter(Boolean).join(' ')}` : null,
          form.suburb ? `Suburb: ${form.suburb}` : null,
          '',
          form.message || '',
        ].filter(x => x !== null).join('\n'),
      });
      toast.success('Enquiry sent to ProClean!');
      setSent(true);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-xl w-full bg-slate-900/80 backdrop-blur-xl border border-cyan-400/20 rounded-3xl p-10 text-center shadow-2xl"
          data-testid="special-thankyou"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/50">
            <Check className="w-10 h-10 text-slate-900" strokeWidth={3} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">Enquiry Sent!</h1>
          <p className="text-slate-300 mb-8 leading-relaxed">
            Thanks {form.name.split(' ')[0]} — we've received your enquiry for {form.services.length === 1 ? form.services[0].toLowerCase() : `${form.services.length} special services`}. The ProClean team will contact you within 24 hours with a tailored quote.
          </p>
          <div className="flex gap-3 justify-center">
            <Button data-testid="special-home-btn" onClick={() => navigate('/')} className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl px-6">Back to Home</Button>
            <Button data-testid="special-again-btn" onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', suburb: '', make: '', model: '', year: '', services: [], message: '' }); }} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-xl px-6">New Enquiry</Button>
          </div>
        </motion.div>
        <Toaster position="top-center" richColors theme="dark" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <button data-testid="special-back-btn" onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-semibold">Back to Home</span>
          </button>
          <div className="bg-cyan-400/10 border border-cyan-400/30 rounded-lg px-3 py-1.5">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">ProClean</div>
            <div className="text-sm font-bold text-white">Special Services</div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* Left: pitch */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 bg-gradient-to-r from-cyan-300 via-white to-cyan-300 bg-clip-text text-transparent">
              Special Services
            </h1>
            <p className="text-white/60 text-lg mb-8 leading-relaxed">
              Paint correction, window tinting, ceramic coating and more — tell us what your car needs and we'll come back with a tailored quote. We come to you, anywhere in Sydney.
            </p>

            <div className="space-y-3 mb-10">
              {SERVICES.map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                    <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div>
                      <div className="font-bold text-white">{s.label}</div>
                      {s.sub && <div className="text-xs text-cyan-300/80 font-semibold">{s.sub}</div>}
                      <div className="text-sm text-white/50 mt-0.5">{s.blurb}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-white/10 pt-6 space-y-2 text-sm text-white/60">
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-cyan-300" /> 0421 619 872</div>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-cyan-300" /> proclean.mobiledetailiing@gmail.com</div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl"
          >
            <h2 className="text-2xl font-extrabold mb-1">Send an enquiry</h2>
            <p className="text-white/50 text-sm mb-6">We reply within 24 hours.</p>

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60 font-bold">Name *</Label>
                <Input data-testid="special-name" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your name" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl h-12 placeholder:text-white/30" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/60 font-bold">Email *</Label>
                  <Input data-testid="special-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl h-12 placeholder:text-white/30" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/60 font-bold">Phone *</Label>
                  <Input data-testid="special-phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="04xx xxx xxx" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl h-12 placeholder:text-white/30" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60 font-bold">Suburb</Label>
                <Input data-testid="special-suburb" value={form.suburb} onChange={(e) => update('suburb', e.target.value)} placeholder="e.g. Bondi" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl h-12 placeholder:text-white/30" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60 font-bold">Services you're interested in * <span className="normal-case font-normal text-white/40">(pick any)</span></Label>
                <div className="grid sm:grid-cols-2 gap-2 mt-1.5">
                  {SERVICES.map(s => {
                    const active = form.services.includes(s.label);
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`special-service-${s.id}`}
                        onClick={() => toggleService(s.label)}
                        className={`text-left p-3.5 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 ${
                          active
                            ? 'border-cyan-400 bg-cyan-400/10 shadow-lg shadow-cyan-500/10'
                            : 'border-white/10 bg-white/5 hover:border-white/25'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center transition-colors ${
                          active ? 'bg-cyan-400' : 'bg-white/5 border-2 border-white/20'
                        }`}>
                          {active && <Check className="w-4 h-4 text-slate-900" strokeWidth={3} />}
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm font-bold leading-tight ${active ? 'text-white' : 'text-white/80'}`}>{s.label}</div>
                          {s.sub && <div className="text-xs text-cyan-300/80 mt-0.5">{s.sub}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {form.services.length > 0 && (
                  <div data-testid="special-services-count" className="mt-2 text-xs font-semibold text-cyan-300">
                    {form.services.length} selected — we'll quote them together
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60 font-bold">Vehicle <span className="normal-case font-normal text-white/40">(optional)</span></Label>
                <div className="grid grid-cols-3 gap-3 mt-1.5">
                  <Input data-testid="special-make" value={form.make} onChange={(e) => update('make', e.target.value)} placeholder="Make" className="bg-white/5 border-white/10 text-white rounded-xl h-12 placeholder:text-white/30" />
                  <Input data-testid="special-model" value={form.model} onChange={(e) => update('model', e.target.value)} placeholder="Model" className="bg-white/5 border-white/10 text-white rounded-xl h-12 placeholder:text-white/30" />
                  <Input data-testid="special-year" value={form.year} onChange={(e) => update('year', e.target.value)} placeholder="Year" className="bg-white/5 border-white/10 text-white rounded-xl h-12 placeholder:text-white/30" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60 font-bold">Tell us what you need</Label>
                <Textarea data-testid="special-message" value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="e.g. Black SUV, swirls on the bonnet, want paint correction and ceramic..." rows={4} className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/30" />
              </div>

              <Button
                data-testid="special-submit"
                onClick={submit}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 rounded-xl h-12 font-extrabold shadow-lg shadow-cyan-500/25"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? 'Sending...' : 'Send Enquiry'}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
};

export default SpecialServices;
